import { useState } from 'react';

import { abbreviate, formatDate, formatMoney, nightCount } from '../format.ts';
import type { BookingView } from '../types.ts';
import { HashStrip, type HashBinding } from './HashStrip.tsx';

interface BookingCardProps {
  readonly booking: BookingView;
  readonly binding: HashBinding;
  readonly busy: boolean;
  readonly onConfirm: (bookingId: string) => void;
  readonly onAmend: (bookingId: string, amountMinor: number) => void;
  /** True while this booking is the one being verified. */
  readonly challenging: boolean;
}

/**
 * One booking, with the two actions the demo needs.
 *
 * "Confirm" carries no amount and no threshold — it sends the booking id and nothing
 * else, which is what makes the server's decision unforgeable rather than merely
 * validated. "Change amount" exists so the tampering scenario can be performed live
 * rather than described.
 */
export function BookingCard({
  booking,
  binding,
  busy,
  onConfirm,
  onAmend,
  challenging,
}: BookingCardProps) {
  const [editing, setEditing] = useState(false);
  const [draftMajor, setDraftMajor] = useState(String(booking.amountMinor / 100));

  const confirmed = booking.status === 'CONFIRMED';
  const nights = nightCount(booking.checkIn, booking.checkOut);
  const cardClass = confirmed
    ? 'card card--sealed'
    : booking.stepUpRequired
      ? 'card card--high'
      : 'card';

  function applyAmendment(): void {
    const parsedMajor = Number(draftMajor);
    if (!Number.isFinite(parsedMajor) || parsedMajor < 0) return;
    // Rounded to whole minor units here so the request carries an integer. The server
    // validates independently; this only avoids sending an obviously broken value.
    onAmend(booking.bookingId, Math.round(parsedMajor * 100));
    setEditing(false);
  }

  return (
    <article
      className={cardClass}
      aria-labelledby={`hotel-${booking.bookingId}`}
      // The booking id is the only stable handle on a card: hotel names repeat across
      // bookings and amounts change when the tampering demo runs. E2E specs address cards
      // through this rather than through text they are also asserting on.
      data-testid={`booking-${booking.bookingId}`}
      data-status={booking.status}
      data-step-up-required={String(booking.stepUpRequired)}
    >
      <div className="card__head">
        <div>
          <h3 id={`hotel-${booking.bookingId}`}>{booking.hotelName}</h3>
          <p className="eyebrow" style={{ marginBottom: 0 }}>
            {booking.bookingId} · {booking.status}
          </p>
        </div>
        <p className="amount">{formatMoney(booking.amountMinor, booking.currency)}</p>
      </div>

      <ul className="stay">
        <li>
          {booking.roomType} · <span className="value">{nights}</span> night
          {nights === 1 ? '' : 's'}
        </li>
        <li>
          <span className="value">{formatDate(booking.checkIn)}</span> to{' '}
          <span className="value">{formatDate(booking.checkOut)}</span>
        </li>
        <li>
          <span className="value">{booking.guests}</span> guest{booking.guests === 1 ? '' : 's'}
        </li>
      </ul>

      <HashStrip hashPrefix={booking.contentHashPrefix} binding={binding} />

      {booking.stepUpRequired && !confirmed ? (
        <p className="muted" style={{ marginTop: '0.875rem' }}>
          Above the{' '}
          <span className="value">{formatMoney(booking.thresholdMinor, booking.currency)}</span>{' '}
          threshold, so confirming it needs a code sent to your email.
        </p>
      ) : null}

      {editing ? (
        <div className="code-entry">
          <div>
            <label htmlFor={`amount-${booking.bookingId}`}>
              Amount ({booking.currency}, major units)
            </label>
            <input
              id={`amount-${booking.bookingId}`}
              type="number"
              min="0"
              step="0.01"
              value={draftMajor}
              onChange={(event) => setDraftMajor(event.target.value)}
              style={{ width: '10rem' }}
            />
          </div>
          <button type="button" onClick={applyAmendment} disabled={busy}>
            Save change
          </button>
          <button
            type="button"
            className="button--quiet"
            onClick={() => {
              setEditing(false);
              setDraftMajor(String(booking.amountMinor / 100));
            }}
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="actions" style={{ marginTop: '0.875rem' }}>
          <button
            type="button"
            className={booking.stepUpRequired && !confirmed ? 'button--signal' : ''}
            onClick={() => onConfirm(booking.bookingId)}
            disabled={busy || confirmed || challenging}
          >
            {confirmed ? 'Confirmed' : booking.stepUpRequired ? 'Verify and confirm' : 'Confirm'}
          </button>
          {confirmed ? null : (
            <button
              type="button"
              className="button--secondary"
              onClick={() => setEditing(true)}
              disabled={busy}
            >
              Change amount
            </button>
          )}
          <span className="hashstrip__label">v{booking.version}</span>
        </div>
      )}

      {challenging ? (
        <p className="muted" style={{ marginTop: '0.75rem' }}>
          Verification in progress · <span className="value">{abbreviate(booking.bookingId)}</span>
        </p>
      ) : null}
    </article>
  );
}
